import type { ReactNode } from "react";

// RadioCard — THE selectable option row for the design system (DS §4.10). One
// row; `RadioCards` maps it when the whole option list lives in one place.
//
// It absorbed SelectableCard, which was the same control wearing a different
// face: a trailing check in a filled circle, an elevation ring instead of a
// border, and a 48px avatar slot. Two treatments for "pick one of these" meant
// CalendarStep rendered both on a single screen — a provider picker with checks
// above a mode picker with dots. The dot won on the operator's ruling and on
// arithmetic: 8 call sites to 1.
//
// The dot is TRAILING. It reads as the row's answer rather than its bullet, it
// keeps the label flush left with the avatar when one is present, and it lands
// where the eye finishes rather than where it starts.
//
// `avatar` is SelectableCard's leading slot, kept because the calendar-provider
// picker needs somewhere for the Google/Outlook logos. It is a rounded SQUARE,
// not a disc: what sits in it is a product logo, and every one of those is
// drawn on a square grid — a circle crops the corners of a mark that was never
// meant to be cropped. --radius-md (8px) inside the row's --radius-lg (10px),
// the smaller inner radius that nested corners want.
//
// The tile is --canvas in BOTH states, not a colour with a meaning. It used to
// invert to --surface when selected, to keep a white-heavy mark legible against
// the sage fill; --canvas already clears that bar on either ground, and holding
// it still means the logo does not flicker as you move down the list. The sage
// says "selected" once, on the row, instead of twice.
//
// --interactive, not --cta, marks the committed state across the selection
// controls (radio, checkbox, switch, CheckList). --cta is the colour of the ONE
// action a screen is pushing you toward; a checked box is not that, it is a
// thing you have already settled, and it should not compete with the primary
// button beside it. It also lines the controls up with the fields, which hover
// and focus on the same token, so "you touched this" reads the same everywhere.
//
// The BORDER token is --interactive and not --success. --success is a STATUS (a
// thing that went well); a control's boundary is not. (This note used to add "even
// though the two carry the same #6E9F94 today" — they no longer do: --success was
// repainted to #2C4C48 on 2026-08-31, which is what makes it usable as the dot's
// fill above while the ring stays --interactive.)
//
// `compact` is the row of a short list on a --muted ground: the time list of the
// in-call tray. The --surface fill already draws the row's edge there, so the
// row has no visible border until it is checked, and its label is --text-sm
// semibold, a step under the day header above the list. The 1px box stays, so
// checking a row changes colour, never size.
//
// Colors use the dova-ds unlayered utility classes so the `.dova-*` cascade does
// not fight a competing arbitrary-value class. Presentation-only.

export interface RadioCardProps {
    name: string;
    value: string;
    label: ReactNode;
    description?: ReactNode;
    checked: boolean;
    onChange: (value: string) => void;
    /** Leading visual — a logo, initials, an <img>, an icon. Rendered in a 48px
     *  round slot that inverts to --surface when selected. Omit for a plain row. */
    avatar?: ReactNode;
    /** Shape of the avatar slot. `square` (default) is the rounded square a
     *  LOGO wants: the calendar-provider step puts Google and Outlook marks in
     *  here, and a circle crops them. `circle` is for INITIALS standing in for a
     *  person, which is the couple picker, and it is opt-in so no existing slot
     *  changes shape. Circle also sets the initial in the serif display face, at
     *  the proportion the welcome screen's pair uses. */
    avatarShape?: "square" | "circle";
    disabled?: boolean;
    /** The row on a --muted ground: no visible border until checked, and a smaller, semibold label. */
    compact?: boolean;
}

export function RadioCard({
    name,
    value,
    label,
    description,
    checked,
    onChange,
    avatar,
    avatarShape = "square",
    disabled,
    compact = false,
}: RadioCardProps) {
    return (
        <label
            className={[
                "flex items-center gap-4 rounded-[var(--radius-lg)]",
                "transition-[border-color,background-color] duration-[var(--dur-base)] ease-[var(--ease-out)]",
                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                // Checked = 1px --interactive border over the --interactive-soft
                // tint: the one selected style every DS selection control shares
                // (RadioCard, CheckableCard, ToggleChips, WordChips, Segmented).
                //
                // ⚠ KNOWN WCAG 1.4.11 GAP, ACCEPTED (operator, 2026-09-15):
                // --interactive #6E9F94 measures 2.54:1 on the tint and 2.58:1 on
                // --canvas, under the 3:1 a control boundary needs. A 2px --success
                // #2C4C48 border (8:1) was merged in 2026-09 to fix that and was
                // reverted after side-by-side review — it read too dark. Selection
                // is also carried by the tint and the filled dot. Do not "fix" this
                // back to --success without going back to design.
                //
                // The fill lives ONLY in the branches. bg-surface used to sit in
                // this base list beside bg-interactive-soft, and Tailwind's
                // stylesheet order let surface win — a checked card got the
                // border but never the tint (selection audit, 2026-09-15).
                checked
                    ? "border border-interactive bg-interactive-soft p-4"
                    : `border ${compact ? "border-transparent" : "border-border"} bg-surface p-4 ${disabled ? "" : "hover:border-interactive"}`,
            ].join(" ")}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                disabled={disabled}
                // Guarded as well as `disabled`, not instead of it. A real browser
                // will not fire a change on a disabled input, so the attribute is
                // what does the work — but that leaves the component's contract
                // resting on the host's activation behaviour rather than on
                // anything here. SelectableCard used a <button disabled>, which
                // blocked outright; this keeps that guarantee explicit.
                onChange={(e) => {
                    if (!disabled) onChange(e.target.value);
                }}
                className="sr-only"
            />

            {avatar !== undefined && (
                <span
                    aria-hidden
                    className={[
                        "flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden bg-canvas text-cta-ink",
                        avatarShape === "circle"
                            // --type-h3 (18px serif), 37% of the 48px slot — the
                            // same proportion the welcome screen's pair holds at
                            // 24-in-64, so the two read as one treatment at two
                            // sizes. The sans `font-semibold` stays on the square,
                            // where the slot is usually holding a logo and the
                            // rare initial should not announce itself as display
                            // type.
                            ? "rounded-full [font:var(--type-h3)]"
                            : "rounded-[var(--radius-md)] font-semibold",
                    ].join(" ")}
                >
                    {avatar}
                </span>
            )}

            <span className="min-w-0 flex-1">
                <span
                    className={`block font-sans leading-[1.3] text-primary ${compact ? "text-sm font-semibold" : "text-base font-medium"}`}
                >
                    {label}
                </span>
                {/* --type-body-sm (14px), not --type-caption (12). A description
                    on a card you are choosing between is reading matter, not a
                    footnote on someone else's row. */}
                {description && (
                    <span className="mt-0.5 block [font:var(--type-body-sm)] text-secondary">
                        {description}
                    </span>
                )}
            </span>

            {/* 24px, up from 20 (2026-09-14): compared side by side against
                filled and haloed variants, the existing surface-and-dot style
                was kept and only the size moved. The dot scales with it. */}
            <span
                aria-hidden
                className={[
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] bg-surface",
                    checked ? "border-interactive" : "border-border",
                ].join(" ")}
            >
                {/* --interactive, and KNOWN to be light: #6E9F94 on --surface measures
                    2.83:1, under the 3:1 WCAG asks of a non-text control. It was moved
                    to --success (8.9:1) on 2026-09-02 and moved straight back the same
                    day — the operator looked at it and the dark dot did not read right
                    on the row.

                    Recorded rather than left as a puzzle, because the next person to
                    measure this will find the same number and be tempted by the same
                    fix. The contrast problem is real and currently UNSOLVED: a 2px
                    --success card border closed it for a while, and was reverted on
                    2026-09-15 as too dark (see the checked branch above). Size and
                    stroke weight cannot change a contrast ratio, so a larger dot or a
                    heavier ring would not close this gap either. The dot is
                    deliberately left light. */}
                <span
                    className={`h-3 w-3 rounded-full bg-interactive transition-transform duration-[var(--dur-base)] ease-[var(--ease-out)] ${
                        checked ? "scale-100" : "scale-0"
                    }`}
                />
            </span>
        </label>
    );
}
